import React, { useState, useEffect } from 'react';
import { TOKENS } from '../tokens';
import api, { getImageUrl } from '../api/client';
import { Truck, CheckSquare, XCircle, FileText, Image as ImageIcon, Send } from 'lucide-react';

export function ManagerDeliveryScreen() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  
  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      const { data } = await api.get('/deliveries');
      setDeliveries(data.data || data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReopenStep = async (stepId) => {
    const reason = prompt("Enter reason to reopen this step:");
    if (!reason) return;
    
    try {
      await api.put(`/deliveries/steps/${stepId}/reopen`, { reason });
      alert("Step reopened.");
      // In a real app, refresh the specific delivery details
      fetchDeliveries(); 
    } catch (err) {
      alert("Failed to reopen step.");
    }
  };

  return (
    <div style={{ padding: 24, display: 'flex', gap: 24, height: 'calc(100vh - 100px)' }}>
      {/* List of deliveries */}
      <div style={{ flex: '0 0 350px', background: '#fff', borderRadius: 16, border: '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 20, borderBottom: '1px solid #eee' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces', color: TOKENS.navyDeep, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Truck color={TOKENS.gold} /> Deliveries
          </h2>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {loading ? <p style={{ padding: 12 }}>Loading...</p> : deliveries.map(d => (
            <div 
              key={d.delivery_id}
              onClick={() => {
                api.get(`/deliveries/${d.delivery_id}`).then(res => setSelectedDelivery(res.data));
              }}
              style={{
                padding: 16, borderRadius: 12, marginBottom: 8, cursor: 'pointer',
                background: selectedDelivery?.delivery_id === d.delivery_id ? TOKENS.navySoft : '#f8f9fa',
                color: selectedDelivery?.delivery_id === d.delivery_id ? '#fff' : TOKENS.navyDeep,
                border: '1px solid #eee'
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>{d.project_name}</div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{d.site_name}</div>
              <div style={{ fontSize: 12, marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>Handover: {new Date(d.handover_date).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Details View */}
      <div style={{ flex: 1, background: '#fff', borderRadius: 16, border: '1px solid #eee', padding: 32, overflowY: 'auto' }}>
        {!selectedDelivery ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
            <CheckSquare size={48} style={{ opacity: 0.2, marginBottom: 16 }} />
            <h3>Select a delivery to view checklists</h3>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
              <div>
                <div style={{ fontSize: 14, color: '#666', fontWeight: 600, marginBottom: 4 }}>{selectedDelivery.project_name}</div>
                <h2 style={{ margin: 0, color: TOKENS.navyDeep, fontSize: 28 }}>{selectedDelivery.site_name}</h2>
                <p style={{ margin: '8px 0 0 0', color: '#555' }}>{selectedDelivery.address}</p>
              </div>
              <button style={{
                background: TOKENS.gold, color: TOKENS.navyDeep, border: 'none', 
                padding: '10px 20px', borderRadius: 8, fontWeight: 700, 
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'
              }}>
                <Send size={18} /> Request Sign-Off
              </button>
            </div>

            <h3 style={{ borderBottom: '2px solid #eee', paddingBottom: 8, color: TOKENS.navyDeep }}>Checklist & Proofs</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
              {selectedDelivery.steps?.map(step => (
                <div key={step.step_id} style={{ 
                  border: '1px solid #eee', borderRadius: 12, padding: 20,
                  background: step.done_at ? '#F8FCF9' : '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ 
                        width: 24, height: 24, borderRadius: '50%', 
                        background: step.done_at ? '#1E8E3E' : '#eee',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff'
                      }}>
                        {step.done_at && <CheckSquare size={14} />}
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 600, color: TOKENS.navyDeep }}>
                        {step.title}
                      </span>
                      <span style={{ fontSize: 12, background: '#eee', padding: '4px 8px', borderRadius: 12, color: '#666' }}>
                        Type: {step.proof_type}
                      </span>
                    </div>
                    {step.done_at && (
                      <button 
                        onClick={() => handleReopenStep(step.step_id)}
                        style={{ background: 'transparent', border: '1px solid #ff4d4f', color: '#ff4d4f', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Reopen (Untick)
                      </button>
                    )}
                  </div>
                  
                  <div style={{ paddingLeft: 36, marginTop: 12, fontSize: 13, color: '#666' }}>
                    Assigned to: {step.assignee_name || 'Unassigned'}
                    {step.done_at && <div style={{ marginTop: 4 }}>Completed at: {new Date(step.done_at).toLocaleString()}</div>}
                  </div>

                  {step.proofs && step.proofs.length > 0 && (
                    <div style={{ paddingLeft: 36, marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {step.proofs.map(proof => (
                        <div key={proof.proof_id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 8, background: '#fff' }}>
                          {step.proof_type === 'photo' ? (
                            <img src={getImageUrl(proof.file_url)} alt="Proof" style={{ height: 100, borderRadius: 4, objectFit: 'cover' }} />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: TOKENS.navyDeep, padding: '10px 20px' }}>
                              <FileText /> Document Proof
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: '#999', marginTop: 8, textAlign: 'center' }}>
                            {proof.lat ? `GPS: ${parseFloat(proof.lat).toFixed(4)}, ${parseFloat(proof.lng).toFixed(4)}` : 'No GPS data'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(!selectedDelivery.steps || selectedDelivery.steps.length === 0) && (
                <p style={{ color: '#888' }}>No delivery steps configured.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
